/**

 * DXF-extracted equipment in 3D — always instanced, with transfer overlays on top.

 */

import React, { useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../hooks/redux';

import {

  equipmentInstanceOrigin,

  equipmentInstanceToLayoutElement,

  equipmentProvenance,

  isEquipmentOverlayVisible,

  polygonBounds,

} from '../../utils/equipmentGeometry';

import {

  EQUIPMENT_3D_DEPTH,

  defaultElementDepth,

  resolveEquipmentRenderHeight,

} from '../../utils/layout3d';

import { resolveEquipment3DPortFacing } from './amhs/Equipment/equipmentFacing3d';

import { resolveKitDockLayout } from './amhs/Equipment/equipmentPortOwnership';

import { selectInstance } from '../../features/equipment/equipmentSlice';

import FabAmhsFloorLayer3D from './FabAmhsFloorLayer3D';
import type { EquipmentRenderDescriptor } from './equipmentSilhouettePorts';

import TransferAnimationLayer3D from './TransferAnimationLayer3D';
import StockerLoadPortsLayer3D from './StockerLoadPortsLayer3D';



const DxfEquipmentInstances3D: React.FC = () => {

  const dispatch = useAppDispatch();

  const order = useAppSelector((s) => s.equipment.instanceOrder);

  const instances = useAppSelector((s) => s.equipment.instances);

  const overlayVisible = useAppSelector((s) => s.equipment.visible);

  const hiddenProcesses = useAppSelector((s) => s.equipment.hiddenProcesses);

  const dxfLayers = useAppSelector((s) => s.floorplan.layers);

  const hiddenEntityTypes = useAppSelector((s) => s.floorplan.hiddenEntityTypes);

  const layoutElements = useAppSelector((s) => s.layout.elements);

  const templates = useAppSelector((s) => s.equipment.templates);

  const savedDxfIds = useMemo(() => {

    const ids = new Set<string>();

    for (const element of Object.values(layoutElements)) {

      const sourceId = element?.customProperties?.dxfEquipmentId;

      if (typeof sourceId === 'string' && sourceId) ids.add(sourceId);

    }

    return ids;

  }, [layoutElements]);



  const { items, totalDxf } = useMemo(() => {

    const items: EquipmentRenderDescriptor[] = [];

    let totalDxf = 0;



    for (const id of order) {

      const inst = instances[id];

      if (!inst || !inst.polygon || inst.polygon.length < 2) continue;

      if (equipmentProvenance(inst) !== 'dxf') continue;

      totalDxf += 1;

      if (savedDxfIds.has(id)) continue;
      if (layoutElements[id]) continue;

      if (

        !isEquipmentOverlayVisible(inst, {

          overlayVisible,

          dxfLayers,

          hiddenEntityTypes,

          hiddenProcesses,

          layoutElements,

        })

      ) {

        continue;

      }



      const bounds = polygonBounds(inst.polygon);

      const width = Math.max(0.5, bounds.maxX - bounds.minX);

      const height = Math.max(0.5, bounds.maxY - bounds.minY);

      const kind = inst.objectType === 'stocker' ? 'stocker' : 'equipment';

      const depth = resolveEquipmentRenderHeight(

        kind,

        width,

        height,

        kind === 'stocker' ? defaultElementDepth('stocker') : EQUIPMENT_3D_DEPTH

      );

      const origin = equipmentInstanceOrigin(inst);

      const template = inst.templateId ? templates[inst.templateId] ?? null : null;

      const simElement = equipmentInstanceToLayoutElement(inst, template);

      const facing = resolveEquipment3DPortFacing(simElement);

      const docks = kind === 'equipment' ? resolveKitDockLayout(simElement, instances) : null;



      items.push({

        id,

        originX: origin.x,

        originY: origin.y,

        width,

        height,

        depth,

        rotationDeg: inst.rotation || 0,

        kind,

        facingRotationY: facing.rotationY,

        swapsFootprintAxes: facing.swapsFootprintAxes,

        loadPortCount: docks?.loadPortCount,

        portFractions: docks?.portFractions,

      });

    }



    return { items, totalDxf };

  }, [

    order,

    instances,

    overlayVisible,

    dxfLayers,

    hiddenEntityTypes,

    hiddenProcesses,

    layoutElements,

    savedDxfIds,

    templates,

  ]);



  useEffect(() => {

    if (!import.meta.env.DEV) return;

    console.info(

      `[3D] DXF equipment: ${items.length} instanced of ${totalDxf} extracted ` +

        `(${order.length} overlay instances total).`

    );

  }, [items.length, totalDxf, order.length]);



  if (items.length === 0) return null;



  return (

    <group>

      <FabAmhsFloorLayer3D
        descriptors={items}
        layoutMeta={layoutElements}
        onSelect={(id) => dispatch(selectInstance(id))}
      />

      <StockerLoadPortsLayer3D descriptors={items} layoutMeta={layoutElements} />

      <TransferAnimationLayer3D descriptors={items} layoutMeta={layoutElements} />

    </group>

  );

};



export default React.memo(DxfEquipmentInstances3D);

